import React, { useState } from "react";

import { useMutation } from "@apollo/client";

import { graphql } from "@/gql";
import useAlert from "@/hooks/useAlert";
import useDialogState from "@/hooks/useDialogState";
import { useFragment } from "@/hooks/useFragment";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { displayError } from "@/utils/errors";
import DeleteModal from "@components/DeleteModal";

import AddBodyMeasurementModal from "./components/AddBodyMeasurementModal";
import BodyMeasurementList from "./components/BodyMeasurementList";
import { clientProfilePageQuery } from "../../ClientProfilePage";

interface Props {
  clientId: string;
}

const bodyMeasurementFragment = graphql(`
  fragment BodyMeasurementSection on Client {
    id
    journalGoal {
      id
      targetWeightKg
    }
    latestHeightCm {
      id
      value
    }
    latestWeightKg {
      id
      value
    }
    profile {
      gender
    }
    setting {
      ...BodyMeasurementListItem_Setting
    }
    latestBodyMeasurements {
      ...BodyMeasurementListItem
    }
  }
`);

const deleteBodyMeasurementsMutation = graphql(`
  mutation DeleteBodyMeasurements($input: DeleteBodyMeasurementsInput!) {
    deleteBodyMeasurements(input: $input) {
      client {
        id
      }
    }
  }
`);

const BodyMeasurement: React.FC<Props> = ({ clientId }) => {
  const [client] = useFragment(bodyMeasurementFragment, `Client:${clientId}`);
  const [deleteBodyMeasurements] = useMutation(deleteBodyMeasurementsMutation);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [modalVisibility, setModalVisibility] = useState<boolean>(false);
  const deleteDialog = useDialogState();
  const snackbar = useGlobalSnackbar();
  const alert = useAlert();

  const targetWeight = client?.journalGoal?.targetWeightKg;
  const settings = client?.setting;
  const clientHeight = client?.latestHeightCm?.value;
  const clientWeight = client?.latestWeightKg?.value;
  const clientGender = client?.profile?.gender;

  const handleCloseModal = () => {
    setModalVisibility(false);
    setIsEditing(false);
  };

  const handleModalOpen = () => {
    setIsEditing(false);
    setModalVisibility(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setModalVisibility(true);
  };

  const handleDelete = async () => {
    if (client == null || client.latestBodyMeasurements == null) return;
    try {
      const result = await deleteBodyMeasurements({
        variables: {
          input: {
            clientId: client.id,
            date: client.latestBodyMeasurements.date,
          },
        },
        refetchQueries: [
          { query: clientProfilePageQuery, variables: { id: client.id } },
        ],
      });
      if (result.data) alert.open("Body measurements deleted successfully.");
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          "Something went wrong while deleting body measurements. Please try again later."
        )
      );
    }
  };

  return (
    <>
      <BodyMeasurementList
        settings={settings}
        toggleAdd={handleModalOpen}
        toggleEdit={handleEdit}
        toggleDelete={deleteDialog.show}
        bodyMeasurements={client?.latestBodyMeasurements}
        targetWeight={targetWeight}
        gender={clientGender}
        latestHeightCm={clientHeight}
        latestWeightKg={clientWeight}
      />
      <AddBodyMeasurementModal
        isEditing={isEditing}
        settings={settings}
        visibility={modalVisibility}
        onClose={handleCloseModal}
      />
      <DeleteModal
        visible={deleteDialog.shown}
        title=""
        message="Are you sure you want to delete body measurements for this date?"
        onDelete={handleDelete}
        onClose={deleteDialog.hide}
      />
    </>
  );
};

export default BodyMeasurement;
